import React, { useState, useEffect } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { useFinancial } from '../../hooks/useFinancial';
import { generateIRReportPDF } from '../../utils/pdfGenerator';
import { formatCurrency } from '../../utils/pdfGenerator';
import './Financial.css';

export const IRReport: React.FC = () => {
  const { irReport, loadIRReport, loading } = useFinancial();
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  useEffect(() => {
    loadIRReport(selectedYear);
  }, [selectedYear, loadIRReport]);

  const handleExportPDF = () => {
    if (irReport.length === 0) {
      alert('Não há dados para exportar');
      return;
    }

    const pdf = generateIRReportPDF(irReport, selectedYear);
    pdf.save(`relatorio-ir-${selectedYear}.pdf`);
  };

  const handleExportExcel = () => {
    if (irReport.length === 0) {
      alert('Não há dados para exportar');
      return;
    }

    // Criar CSV (simples, pode ser melhorado com biblioteca específica)
    let csv = 'Mês,Total,Consultas,Média\n';
    irReport.forEach((receita) => {
      const [year, month] = receita.periodo.split('-');
      const monthNames = [
        'Janeiro',
        'Fevereiro',
        'Março',
        'Abril',
        'Maio',
        'Junho',
        'Julho',
        'Agosto',
        'Setembro',
        'Outubro',
        'Novembro',
        'Dezembro',
      ];
      const monthName = monthNames[parseInt(month) - 1];
      csv += `${monthName}/${year},${receita.total.toFixed(2)},${receita.consultas},${receita.media.toFixed(2)}\n`;
    });

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `relatorio-ir-${selectedYear}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const totalGeral = irReport.reduce((sum, r) => sum + r.total, 0);
  const totalConsultas = irReport.reduce((sum, r) => sum + r.consultas, 0);
  const mediaGeral = totalConsultas > 0 ? totalGeral / totalConsultas : 0;

  return (
    <Card
      title="Relatório para Imposto de Renda"
      subtitle="Receitas por período para declaração de IR"
      actions={
        <div style={{ display: 'flex', gap: '8px' }}>
          <select
            className="ns-input"
            value={selectedYear}
            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            style={{ width: 'auto', padding: '8px' }}
          >
            {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
          <Button variant="secondary" onClick={handleExportPDF} disabled={loading || irReport.length === 0}>
            Exportar PDF
          </Button>
          <Button variant="secondary" onClick={handleExportExcel} disabled={loading || irReport.length === 0}>
            Exportar Excel
          </Button>
        </div>
      }
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <p>Carregando relatório...</p>
        </div>
      ) : irReport.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <p>Nenhuma receita encontrada para o ano {selectedYear}.</p>
        </div>
      ) : (
        <div>
          <div className="ns-grid ns-grid-4" style={{ marginBottom: '24px' }}>
            <div className="ns-kpi">
              <span className="ns-kpi-label">Total do Ano</span>
              <span className="ns-kpi-value">{formatCurrency(totalGeral)}</span>
            </div>
            <div className="ns-kpi">
              <span className="ns-kpi-label">Total de Consultas</span>
              <span className="ns-kpi-value">{totalConsultas}</span>
            </div>
            <div className="ns-kpi">
              <span className="ns-kpi-label">Média por Consulta</span>
              <span className="ns-kpi-value">{formatCurrency(mediaGeral)}</span>
            </div>
            <div className="ns-kpi">
              <span className="ns-kpi-label">Meses com Receita</span>
              <span className="ns-kpi-value">{irReport.length}</span>
            </div>
          </div>

          <div className="ns-table-wrapper">
            <table className="ns-table">
              <thead>
                <tr>
                  <th>Mês</th>
                  <th>Total</th>
                  <th>Consultas</th>
                  <th>Média</th>
                </tr>
              </thead>
              <tbody>
                {irReport.map((receita) => {
                  const [year, month] = receita.periodo.split('-');
                  const monthNames = [
                    'Janeiro',
                    'Fevereiro',
                    'Março',
                    'Abril',
                    'Maio',
                    'Junho',
                    'Julho',
                    'Agosto',
                    'Setembro',
                    'Outubro',
                    'Novembro',
                    'Dezembro',
                  ];
                  const monthName = monthNames[parseInt(month) - 1];

                  return (
                    <tr key={receita.periodo}>
                      <td>
                        <strong>{monthName}/{year}</strong>
                      </td>
                      <td>{formatCurrency(receita.total)}</td>
                      <td>{receita.consultas}</td>
                      <td>{formatCurrency(receita.media)}</td>
                    </tr>
                  );
                })}
                <tr style={{ backgroundColor: 'var(--page-surface-hover)', fontWeight: 'bold' }}>
                  <td>TOTAL</td>
                  <td>{formatCurrency(totalGeral)}</td>
                  <td>{totalConsultas}</td>
                  <td>{formatCurrency(mediaGeral)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <details style={{ marginTop: '24px' }}>
            <summary style={{ cursor: 'pointer', fontWeight: 'bold', marginBottom: '12px' }}>
              Ver Detalhamento por Consulta
            </summary>
            {irReport.map((receita) => {
              const [year, month] = receita.periodo.split('-');
              const monthNames = [
                'Janeiro',
                'Fevereiro',
                'Março',
                'Abril',
                'Maio',
                'Junho',
                'Julho',
                'Agosto',
                'Setembro',
                'Outubro',
                'Novembro',
                'Dezembro',
              ];
              const monthName = monthNames[parseInt(month) - 1];

              return (
                <div key={receita.periodo} style={{ marginBottom: '24px' }}>
                  <h4 style={{ marginBottom: '12px' }}>{monthName}/{year}</h4>
                  <div className="ns-table-wrapper">
                    <table className="ns-table">
                      <thead>
                        <tr>
                          <th>Data</th>
                          <th>Paciente</th>
                          <th>Tipo</th>
                          <th>Valor</th>
                        </tr>
                      </thead>
                      <tbody>
                        {receita.detalhes.map((detalhe, idx) => (
                          <tr key={idx}>
                            <td>{new Date(detalhe.data).toLocaleDateString('pt-BR')}</td>
                            <td>{detalhe.pacienteNome}</td>
                            <td>{detalhe.tipo === 'primeira' ? 'Primeira' : 'Retorno'}</td>
                            <td>{formatCurrency(detalhe.valor)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })}
          </details>
        </div>
      )}
    </Card>
  );
};








